import type { NonMemberBookableOrganizationRecord, PaymentOptionDetail } from '@navio/nsdk';
import { PaymentOption } from '@navio/nsdk';

/**
 * A company car booked by someone who is not a member of the owning company.
 * Most service organizations leave this off. Payment options then omit
 * `nonMemberBookableOrganization`, and nothing here applies.
 */
export type OrganizationCarBooking = {
  userGroupId: number;
  organizationName: string;
  invoiceReferenceRequired: boolean;
  /** The company's terms. Invoice is added beside the customer's own methods. */
  paymentOptions: PaymentOptionDetail[];
};

type BookableOrganization = Pick<
  NonMemberBookableOrganizationRecord,
  'userGroupId' | 'name' | 'paymentOptions' | 'invoiceReferenceRequired'
>;

/**
 * Null unless the API names a company and the customer is not already booking
 * as a company they belong to. That picked company wins.
 */
export function organizationCarBooking(args: {
  ownsBookingOrganization: boolean;
  nonMemberBookableOrganization: BookableOrganization | null | undefined;
}): OrganizationCarBooking | null {
  const organization = args.nonMemberBookableOrganization;
  if (args.ownsBookingOrganization || organization == null) {
    return null;
  }
  return {
    userGroupId: organization.userGroupId,
    organizationName: organization.name,
    invoiceReferenceRequired: organization.invoiceReferenceRequired,
    paymentOptions: paymentOptionDetails(organization.paymentOptions)
  };
}

/**
 * The customer's methods, with the company's invoice added when it invoices.
 * Pay now stays. Null options stay null so checkout can wait for them.
 */
export function paymentOptionsWithOrganizationInvoice(
  own: PaymentOptionDetail[] | undefined,
  organization: OrganizationCarBooking | null
): PaymentOptionDetail[] | undefined {
  if (own == null || organization == null) {
    return own;
  }
  const invoice = organization.paymentOptions.find((detail) => detail.option === PaymentOption.INVOICE);
  if (!invoice?.enabled || invoice.disabledByServiceOrganization) {
    return own;
  }
  return [...own.filter((detail) => detail.option !== PaymentOption.INVOICE), invoice];
}

/**
 * Reference collected on an invoice for that company. Null when this booking
 * is not one, or the customer is not paying by invoice.
 */
export function organizationCarInvoiceReferenceRule(
  party: { kind: 'member' } | { kind: 'nonMember'; invoiceReferenceRequired: boolean },
  isInvoice: boolean
): { required: boolean } | null {
  if (party.kind !== 'nonMember' || !isInvoice) {
    return null;
  }
  return { required: party.invoiceReferenceRequired };
}

function paymentOptionDetails(offered: PaymentOption[]): PaymentOptionDetail[] {
  return Object.values(PaymentOption).map((option) => {
    const enabled = offered.includes(option);
    return { option, enabled, disabledByServiceOrganization: !enabled };
  });
}
